import { createStore } from '../../../../core/utils/stateManagers';
import { createSearchResultBucketKey, type SearchResultWithView } from '../../../utils/searchResultBucket';

// 검색 캐시 항목의 타입
interface CacheItem {
    results: SearchResultWithView[];
    totalElements: number;
}

// 결과 스토어 상태 인터페이스
export interface ResultState {
    searchResults: SearchResultWithView[];
    searchCache: Map<string, CacheItem>;
}

// 초기 상태
const initialState: ResultState = {
    searchResults: [],
    searchCache: new Map(),
};

// 결과 스토어 생성
export const createResultStore = () => {
    const store = createStore<ResultState>(initialState);

    return {
        ...store,

        // 검색 결과 설정
        setSearchResults: (results: SearchResultWithView[]) => {
            store.update(state => ({ ...state, searchResults: results }));
        },

        // 검색 결과 병합 (병렬 executeSearch 호출 race-safe)
        // 초기 탭별 5개 병렬 호출이 각자 자기 카테고리 결과만 들고 있을 때
        // setSearchResults 로 덮어쓰면 마지막 호출만 살아남는다.
        // searchViewType + categoryId 기준으로 dedupe 하여 union 을 유지한다.
        // background prefetch 가 진행 중에 사용자가 동일 탭을 클릭해 동일 카테고리가
        // 두 번 append 되더라도 중복 없이 최신값으로 교체된다.
        appendSearchResults: (incoming: SearchResultWithView[]) => {
            if (!incoming || incoming.length === 0) return;
            store.update(state => {
                const byId = new Map<string, SearchResultWithView>();
                for (const item of state.searchResults) byId.set(createSearchResultBucketKey(item), item);
                for (const item of incoming) byId.set(createSearchResultBucketKey(item), item);
                return { ...state, searchResults: Array.from(byId.values()) };
            });
        },

        /**
         * 검색 결과 초기화
         * @param clearResultsOnly - 캐시는 초기화하지 않고 검색 결과만 초기화할지 여부
         */
        clearSearchResults: (clearResultsOnly = false) => {
            store.update(state => {
                const newState = { ...state, searchResults: [] };
                if (!clearResultsOnly) {
                    // searchCache만 초기화하고, categoryTotalsMap은 외부에서 관리
                    return { ...newState, searchCache: new Map() };
                }
                return newState;
            });
        },

        // 캐시 초기화
        clearSearchCache: () => store.update(state => ({ ...state, searchCache: new Map() })),

        // 캐시 키 생성
        constructSearchCacheKey: (query: string, countryValue: string | null, categoryId: string, page: number, tags: string[] = [], searchViewType?: string | null): string => {
            return JSON.stringify({ query: query.trim(), country: countryValue, categoryId, searchViewType: searchViewType ?? null, page, tags });
        },

        // 캐시에서 검색 결과 가져오기
        getFromSearchCache: (cacheKey: string): CacheItem | undefined => {
            return store.getState().searchCache.get(cacheKey);
        },

        // 캐시에 해당 키가 있는지 확인
        hasInSearchCache: (cacheKey: string): boolean => {
            return store.getState().searchCache.has(cacheKey);
        },

        // API 결과 또는 캐시 결과 처리
        processApiOrCacheResults: (
            apiResponseData: SearchResultWithView[],
            newlyFetchedCategoryKeys: Set<string>,
            allCategoriesForViewAndPage: { categoryId: string; searchViewType?: string; tagCodes: string[] }[],
            fetchedPage: number,
            currentSearchQuery: string,
            currentCountryValue: string | null,
            currentCategoryTotalsMapFromParam: Map<string, number>, // 파라미터 추가
        ): {
            newSearchResults: SearchResultWithView[];
            newCategoryTotalsMap: Map<string, number>;
            categoryTotalsActuallyUpdated: boolean;
        } => {
            // const state = store.getState(); // 이 라인은 searchCache 직접 접근하지 않으면 불필요
            // currentCategoryTotalsMap은 파라미터로 받아서 사용

            const categoryResultsFromApi = new Map<string, SearchResultWithView[]>();
            const viewResultsFromApi = new Map<string, SearchResultWithView[]>();
            apiResponseData.forEach(item => {
                const categoryIdStr = String(item.categoryId);
                if (!categoryResultsFromApi.has(categoryIdStr)) {
                    categoryResultsFromApi.set(categoryIdStr, []);
                }
                categoryResultsFromApi.get(categoryIdStr)?.push(item);

                if (item.searchViewType) {
                    if (!viewResultsFromApi.has(item.searchViewType)) {
                        viewResultsFromApi.set(item.searchViewType, []);
                    }
                    viewResultsFromApi.get(item.searchViewType)?.push(item);
                }
            });

            const newPageResultsAccumulator: SearchResultWithView[] = [];
            const tempCategoryTotalsMap = new Map(currentCategoryTotalsMapFromParam); // 전달받은 맵으로 초기화
            let categoryTotalsActuallyUpdated = false;

            const newlyFetchedCategories = allCategoriesForViewAndPage.filter(category => {
                const categoryKey = createSearchResultBucketKey(category);
                return newlyFetchedCategoryKeys.has(categoryKey) || newlyFetchedCategoryKeys.has(category.categoryId);
            });
            const newlyFetchedCategoryKeySet = new Set(newlyFetchedCategories.map(createSearchResultBucketKey));
            const hasViewAnnotatedResponses = viewResultsFromApi.size > 0;

            // view에서 categoryId 정확 매칭이 하나도 없을 때에만 fallback을 허용해 다중 귀속을 막는다.
            const viewsWithoutExactCategoryMatch = new Set<string>();
            viewResultsFromApi.forEach((responses, viewKey) => {
                const hasExactMatch = responses.some(response =>
                    newlyFetchedCategories.some(category => category.searchViewType === viewKey && category.categoryId === String(response.categoryId)),
                );
                if (!hasExactMatch) {
                    viewsWithoutExactCategoryMatch.add(viewKey);
                }
            });

            // fallback 허용 view의 첫 번째 요청 카테고리 하나만 응답을 받는다.
            const fallbackCategoryKeyByView = new Map<string, string>();
            newlyFetchedCategories.forEach(category => {
                const viewKey = category.searchViewType || '';
                if (!viewsWithoutExactCategoryMatch.has(viewKey)) return;
                if (!fallbackCategoryKeyByView.has(viewKey)) {
                    fallbackCategoryKeyByView.set(viewKey, createSearchResultBucketKey(category));
                }
            });

            const getApiResultsForCategory = (categoryInfo: { categoryId: string; searchViewType?: string }): SearchResultWithView[] => {
                const viewKey = categoryInfo.searchViewType || '';
                const resultsForView = viewKey ? viewResultsFromApi.get(viewKey) || [] : [];
                if (resultsForView.length === 0) {
                    return categoryResultsFromApi.get(categoryInfo.categoryId) || [];
                }

                const matchedByCategoryId = resultsForView.filter(item => String(item.categoryId) === categoryInfo.categoryId);
                if (matchedByCategoryId.length > 0) {
                    return matchedByCategoryId;
                }

                const isFallbackHolder = fallbackCategoryKeyByView.get(viewKey) === createSearchResultBucketKey(categoryInfo);
                return isFallbackHolder ? resultsForView : [];
            };

            newlyFetchedCategories.forEach(categoryInfo => {
                const categoryKey = createSearchResultBucketKey(categoryInfo);
                const categoryIdStr = categoryInfo.categoryId;
                const tagCodes = categoryInfo?.tagCodes || [];
                const sameCategoryInfos = newlyFetchedCategories.filter(category => category.categoryId === categoryIdStr);
                const sameCategoryResponses = categoryResultsFromApi.get(categoryIdStr) || [];
                const sameCategoryIndex = sameCategoryInfos.findIndex(category => createSearchResultBucketKey(category) === categoryKey);
                const newCategoryData = hasViewAnnotatedResponses
                    ? getApiResultsForCategory(categoryInfo)
                    : sameCategoryResponses.length > 1 && sameCategoryIndex >= 0
                      ? [sameCategoryResponses[sameCategoryIndex]].filter(Boolean)
                      : sameCategoryResponses;
                const validResults = newCategoryData
                    .filter(item => item.results?.content?.length && item.results?.content?.length > 0)
                    .map(item => ({
                        ...item,
                        searchViewType: categoryInfo.searchViewType,
                    }));
                // view fallback이 여러 응답을 한 카테고리에 귀속시킬 때 total이 누락되지 않도록 합산한다.
                const totalElements = newCategoryData.reduce((sum, item) => sum + (item.results?.totalElements ?? 0), 0);

                const currentCacheKey = searchResultStore.constructSearchCacheKey(currentSearchQuery, currentCountryValue, categoryIdStr, fetchedPage, tagCodes, categoryInfo.searchViewType);
                const legacyCacheKey = searchResultStore.constructSearchCacheKey(currentSearchQuery, currentCountryValue, categoryIdStr, fetchedPage, tagCodes);

                // addToSearchCache는 내부적으로 store.getState()를 사용하므로 그대로 둠
                store.getState().searchCache.set(currentCacheKey, { results: validResults, totalElements });
                store.getState().searchCache.set(legacyCacheKey, { results: validResults, totalElements });

                if (tempCategoryTotalsMap.get(categoryKey) !== totalElements) {
                    tempCategoryTotalsMap.set(categoryKey, totalElements);
                    categoryTotalsActuallyUpdated = true;
                }

                newPageResultsAccumulator.push(...validResults);
            });

            // 캐시에서 나머지 카테고리 처리
            allCategoriesForViewAndPage.forEach(category => {
                const categoryKey = createSearchResultBucketKey(category);
                if (newlyFetchedCategoryKeySet.has(categoryKey)) return;

                const cacheKey = searchResultStore.constructSearchCacheKey(currentSearchQuery, currentCountryValue, category.categoryId, fetchedPage, category.tagCodes, category.searchViewType);
                const legacyCacheKey = searchResultStore.constructSearchCacheKey(currentSearchQuery, currentCountryValue, category.categoryId, fetchedPage, category.tagCodes);

                // getFromSearchCache는 내부적으로 store.getState()를 사용하므로 그대로 둠
                const cachedData = store.getState().searchCache.get(cacheKey) || store.getState().searchCache.get(legacyCacheKey);
                if (cachedData) {
                    newPageResultsAccumulator.push(...cachedData.results);
                    if (tempCategoryTotalsMap.get(categoryKey) !== cachedData.totalElements) {
                        tempCategoryTotalsMap.set(categoryKey, cachedData.totalElements);
                        categoryTotalsActuallyUpdated = true;
                    }
                }
            });

            return {
                newSearchResults: newPageResultsAccumulator,
                newCategoryTotalsMap: tempCategoryTotalsMap,
                categoryTotalsActuallyUpdated,
            };
        },

        // 스토어 초기화
        reset: () => store.set(initialState),
    };
};

// 싱글톤 인스턴스 생성 및 내보내기
export const searchResultStore = createResultStore();
